import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { LoadingType, setAccount, setLoadingState } from 'actions';
import { setDashboardStep } from 'actions/stages';
import { VerificationState } from 'api/TruvApi';
import { Button } from 'components/Buttons/Button';
import DashboardFormHeader from 'components/DashboardFormHeader';
import DashboardTopInfo from 'components/DashboardTopInfo';
import Loader from 'components/Loader';
import Sidebar, { SidebarElement } from 'components/Sidebar';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { ApplicationType } from 'enums/ApplicationType';
import { BorrowerRole } from 'enums/BorrowerRole';
import { BorrowerType } from 'enums/BorrowerType';
import { ButtonType } from 'enums/ButtonType';
import { DashboardSidebarElements } from 'enums/DashboardSidebarElements';
import { DashboardStep } from 'enums/DashboardStep';
import { DocumentCategory } from 'enums/DocumentCategory';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import DeclineOfferPopup from 'pages/Dashboard/DeclineOfferPopup';
import KbaPopup from 'pages/Dashboard/Identity/KbaPopup';
import NotFoundContact from 'pages/NotFound/NotFoundContact';
import { getAccount } from 'thunks';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import { calculateDueDate } from 'utils/calculateDueDate';
import { TRUV_DISABLED_STATUS } from 'utils/constants';
import {
  getCountNotCompletedRequiredDocuments,
  getCountBorrowerRequiredDocuments,
  isRequiredDocumentsBelongsToCategory,
  isDocumentCategoryCompleted,
} from 'utils/documentsUtils';
import { ApplicationProductName } from 'enums/common.enums';
import { getCompleteInformationDueDateMessage, getCurrentLabel } from 'pages/Dashboard/dashboard.utils';
import { DashboardTabName } from 'enums/DashboardTabName';
import { BorrowerInfo } from 'pages/Dashboard/dashboard.types';
import AcceptOffer from 'pages/Dashboard/steps/AcceptOffer/AcceptOffer/AcceptOffer';
import { updateApplicationInStore } from 'utils/updateApplicationInStore';
import AdditionalInformationTab from 'pages/Dashboard/steps/InformationTabs/AdditionalInformationTab';
import { getIsEmploymentSectionCompleted } from 'pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import LoanOfferOptions from 'pages/Dashboard/steps/AcceptOffer/AcceptOffer/LoanOfferOptions/LoanOfferOptions';
import InformationalBoxWithBubble from 'components/InformationBox';
import VerificationBoxPrompt from 'components/InformationBox/VerificationBoxPrompt';
import AcceptPosAutomatedOffer from 'pages/Dashboard/steps/AcceptOffer/AcceptAutomatedOffer/AcceptPosAutomatedOffer';
import AcceptHilAutomatedOffer from 'pages/Dashboard/steps/AcceptOffer/AcceptAutomatedOffer/AcceptHilAutomatedOffer';
import { applicationApi } from 'api/ApplicationApi';

import {
  DASHBOARD_STEPS_WITH_UPLOADING_DOCUMENTS,
  DISABLED_FUNDING_TOOLTIP_TEXT,
  STATUSES_FOR_LOAN_DETAILS,
  STATUSES_FOR_WITHDRAW_APPLICATION,
  WITHDRAW_APPLICATION,
} from './dashboard.constants';
import styles from './Dashboard.module.scss';
import AccountSettings from './AccountSettings/AccountSettings';
import FAQ from './FAQ';
import BorrowerInformationTab from './steps/InformationTabs/BorrowerInformationTab';
import FundingInformationTab from './steps/InformationTabs/FundingInformationTab';
import NewInquiry from './steps/NewInquiry';
import NoActiveApplication from './steps/NoActiveApplication';
import { Tab } from './TabsPanel/TabsPanel';
import HomeOwnershipTab from './steps/InformationTabs/HomeOwnershipTab/HomeOwnershipTab';
import WorkOrderTab from './steps/InformationTabs/WorkOrderTab';
import AcceptedOfferDetails from './AcceptedOfferDetails';
import MessageFromHfsPopup from './MessageFromHfsPopup';

const getPageByStep = (step: DashboardStep | null) => {
  switch (step) {
    case DashboardStep.NewInquiry:
      return <NewInquiry />;

    case DashboardStep.AcceptOffer:
      return <AcceptOffer />;

    case DashboardStep.AcceptPosAutomatedOffer:
      return <AcceptPosAutomatedOffer />;

    case DashboardStep.AcceptHilAutomatedOffer:
      return <AcceptHilAutomatedOffer />;

    case DashboardStep.FundingInformation:
      return <FundingInformationTab />;

    case DashboardStep.AdditionalInformation:
      return <AdditionalInformationTab />;

    case DashboardStep.BorrowerInformation:
      return <BorrowerInformationTab borrowerType={BorrowerRole.Borrower} />;

    case DashboardStep.CoBorrowerInformation:
      return <BorrowerInformationTab borrowerType={BorrowerRole.CoBorrower} />;

    case DashboardStep.HomeOwnership:
      return <HomeOwnershipTab />;

    case DashboardStep.ContractorWorkOrder:
      return <WorkOrderTab />;

    case DashboardStep.FAQ:
      return <FAQ />;

    case DashboardStep.NoActiveApplication:
      return <NoActiveApplication />;

    case DashboardStep.AccountSettings:
      return <AccountSettings />;

    case DashboardStep.LoanDetails:
      return <AcceptedOfferDetails />;

    case DashboardStep.NotFoundContact:
      return <NotFoundContact />;

    default:
      return <NewInquiry />;
  }
};

const getDashboardFormHeader = (
  step: DashboardStep | null,
  messageFromHFS: string | undefined,
  documentsUploadDueDate: string | null,
  tabsPanelUploadDocsAndRequireInfo: Tab[],
) => {
  switch (step) {
    case DashboardStep.BorrowerInformation:
    case DashboardStep.CoBorrowerInformation:
    case DashboardStep.FundingInformation:
    case DashboardStep.HomeOwnership:
    case DashboardStep.ContractorWorkOrder:
    case DashboardStep.AdditionalInformation:
      return (
        <DashboardFormHeader
          isSeparate
          mainMessage={messageFromHFS}
          infoMessage={getCompleteInformationDueDateMessage(documentsUploadDueDate)}
          tabsPanelProps={{
            dashboardTabs: tabsPanelUploadDocsAndRequireInfo,
          }}
        />
      );
  }
};

const Dashboard = () => {
  const { isIframe } = useAppSelector((state) => state.data);
  const currentDashboardStep = useAppSelector((state) => state.stages.currentDashboardStep);
  const { email } = useAppSelector((state) => state.account);
  const {
    applicationId,
    displayId,
    [LoanInformationVariables.LoanAmount]: loanAmount,
    createdDate: createdDate,
    [LoanInformationVariables.ProcessingStatusSetDate]: processingStatusSetDate,
    applicationStatus,
    [LoanInformationVariables.AssignedProcessor]: processorInfo,
    [LoanInformationVariables.LoanType]: applicationType,
    messageFromHFS,
    variables: applicationVariables,
    productName,
    messageFromHfsShowOnPortal,
    dashboardAdditionalInfo,
  } = useAppSelector((state) => state.application);
  const { firstName, lastName, borrowerId } = useAppSelector((state) => state.borrower);
  const { firstName: coBorrowerFirstName } = useAppSelector((state) => state.coBorrower);
  const {
    fundingInformationComplete,
    borrowerPayrollProviderIsDisabled,
    coBorrowerPayrollProviderIsDisabled,
    borrowerInformationRequired,
    coBorrowerInformationRequired,
    fundingInformationIsRequired,
    payrollProviderBorrowerVerificationStates,
    payrollProviderCoBorrowerVerificationStates,
    isThereNonBorrowingCoOwner,
    homeOwnershipInformationComplete,
    borrowerEmploymentInformationComplete,
    coBorrowerEmploymentInformationComplete,
    autopaySectionIsRequired,
    autopaySectionIsCompleted,
  } = useAppSelector((state) => state.dashboardPortal);
  const isTruvDisabled = process.env.REACT_APP_TRUV_STATUS === TRUV_DISABLED_STATUS;
  const [isDeclineOfferOpen, setIsDeclineOfferOpen] = useState(false);
  const { requiredDocuments } = useAppSelector((state) => state.documents);
  const dashboardPageUpdating = useAppSelector((state) => state.loading[LoadingType.DashboardPageUpdating]);
  const dispatch = useAppDispatch();
  const documentsUploadDueDate = calculateDueDate(processingStatusSetDate);
  const isUploadingDocumentStep = DASHBOARD_STEPS_WITH_UPLOADING_DOCUMENTS.includes(currentDashboardStep!);
  const additionalTabProps = dashboardAdditionalInfo!;

  const getCountIncomeSourceVerifications = (verificationState: VerificationState[]) => {
    let verificationsLeft = 0;

    verificationState.map((verification) => {
      if (verification.verificationRequired && !verification.verificationAttemptComplete) {
        verificationsLeft++;
      }
    });

    return verificationsLeft;
  };

  const handleCloseMessageFromHfsPopup = async (): Promise<void> => {
    const response = await applicationApi.markMessageFromHfsAsRead(applicationId!);

    updateApplicationInStore(response.application, dispatch);
  };

  const calculateAdditionalTabRemaining = useMemo(() => {
    const borrowerCurrentEmploymentRemaining = ~~!getIsEmploymentSectionCompleted(
      additionalTabProps,
      BorrowerRole.Borrower,
    );

    const contractorEmploymentRemaining = additionalTabProps.isContractorEmploymentRequired
      ? ~~!additionalTabProps.isContractorEmploymentCompleted
      : 0;

    if (applicationType === ApplicationType.Joint) {
      const coBorrowerCurrentEmploymentRemaining = ~~!getIsEmploymentSectionCompleted(
        additionalTabProps,
        BorrowerRole.CoBorrower,
      );

      return borrowerCurrentEmploymentRemaining + coBorrowerCurrentEmploymentRemaining + contractorEmploymentRemaining;
    }

    return borrowerCurrentEmploymentRemaining + contractorEmploymentRemaining;
  }, [applicationType, dashboardAdditionalInfo]);

  const tabsPanelUploadDocsAndRequireInfo: Tab[] = useMemo(() => {
    const changeTab = async (step: DashboardStep, name: DashboardTabName) => {
      dispatch(setDashboardStep(step));
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${name}`,
          loading: true,
        }),
      );
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${name}`,
          loading: false,
        }),
      );
    };

    const getBorrowerInfo = (borrowerType: BorrowerType): BorrowerInfo => {
      return {
        isInformationRequired:
          borrowerType === BorrowerType.Borrower ? borrowerInformationRequired : coBorrowerInformationRequired,
        payrollProviderIsDisabled:
          borrowerType === BorrowerType.Borrower
            ? borrowerPayrollProviderIsDisabled
            : coBorrowerPayrollProviderIsDisabled,
        payrollProviderVerificationStates:
          borrowerType === BorrowerType.Borrower
            ? payrollProviderBorrowerVerificationStates
            : payrollProviderCoBorrowerVerificationStates,
        employmentInformationComplete:
          borrowerType === BorrowerType.Borrower
            ? borrowerEmploymentInformationComplete
            : coBorrowerEmploymentInformationComplete,
      };
    };

    const countRemainingDocumentsForBorrowerInfo = (borrowerType: BorrowerType) => {
      const borrowerInfo = getBorrowerInfo(borrowerType);

      if (!borrowerInfo.isInformationRequired) {
        return 0;
      }

      const incomeSourceVerificationsCount =
        isTruvDisabled || borrowerInfo.payrollProviderIsDisabled
          ? 0
          : getCountIncomeSourceVerifications(borrowerInfo.payrollProviderVerificationStates);
      const requiredBorrowerDocumentsCount = getCountBorrowerRequiredDocuments(requiredDocuments, borrowerType);
      let smallLoanProgramEmploymentsInfoCount = 0;

      if (productName === ApplicationProductName.SmallLoanProgram) {
        smallLoanProgramEmploymentsInfoCount = borrowerInfo.employmentInformationComplete ? 0 : 1;
      }

      return incomeSourceVerificationsCount + requiredBorrowerDocumentsCount + smallLoanProgramEmploymentsInfoCount;
    };

    return [
      {
        id: DashboardStep.BorrowerInformation,
        name: `${firstName}'s Info`,
        isExist: !!borrowerInformationRequired,
        onClick: async () => changeTab(DashboardStep.BorrowerInformation, DashboardTabName.BorrowerInformation),
        countRemaining: countRemainingDocumentsForBorrowerInfo(BorrowerType.Borrower),
        isDisabled: false,
      },
      {
        id: DashboardStep.CoBorrowerInformation,
        name: `${coBorrowerFirstName}'s Info`,
        isExist: applicationType === ApplicationType.Joint && !!coBorrowerInformationRequired,
        onClick: async () => changeTab(DashboardStep.CoBorrowerInformation, DashboardTabName.CoBorrowerInformation),
        countRemaining: countRemainingDocumentsForBorrowerInfo(BorrowerType.CoBorrower),
        isDisabled: false,
      },
      {
        id: DashboardStep.HomeOwnership,
        name: 'Homeownership',
        isExist:
          isRequiredDocumentsBelongsToCategory(requiredDocuments, DocumentCategory.HomeVerification) ||
          isThereNonBorrowingCoOwner,
        onClick: async () => changeTab(DashboardStep.HomeOwnership, DashboardTabName.HomeOwnership),
        countRemaining:
          getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.HomeVerification) +
          (isThereNonBorrowingCoOwner ? +!homeOwnershipInformationComplete : 0),
        isDisabled: false,
      },
      {
        id: DashboardStep.ContractorWorkOrder,
        name: 'Work Order',
        isExist: isRequiredDocumentsBelongsToCategory(requiredDocuments, DocumentCategory.ContractorWorkOrder),
        onClick: async () => changeTab(DashboardStep.ContractorWorkOrder, DashboardTabName.WorkOrder),
        countRemaining: getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.ContractorWorkOrder),
        isDisabled: false,
      },
      {
        id: DashboardStep.FundingInformation,
        name: 'Funding',
        tooltipTitle: DISABLED_FUNDING_TOOLTIP_TEXT,
        isExist:
          fundingInformationIsRequired ||
          autopaySectionIsRequired ||
          isRequiredDocumentsBelongsToCategory(requiredDocuments, DocumentCategory.Funding),
        onClick: async () => {
          dispatch(setDashboardStep(DashboardStep.FundingInformation));
        },
        countRemaining:
          (fundingInformationIsRequired ? ~~!fundingInformationComplete : 0) +
          getCountNotCompletedRequiredDocuments(requiredDocuments, DocumentCategory.Funding) +
          (autopaySectionIsRequired ? ~~!autopaySectionIsCompleted : 0),
        isDisabled:
          !isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.BorrowerID) ||
          (applicationType === ApplicationType.Joint
            ? !isDocumentCategoryCompleted(requiredDocuments, DocumentCategory.CoborrowerID)
            : false),
      },
      {
        id: DashboardStep.AdditionalInformation,
        name: 'Additional Info',
        isExist: additionalTabProps.isAdditionalInfoTabRequired,
        onClick: async () => changeTab(DashboardStep.AdditionalInformation, DashboardTabName.AdditionalInformation),
        countRemaining: calculateAdditionalTabRemaining,
        isDisabled: false,
      },
    ];
  }, [
    firstName,
    borrowerInformationRequired,
    coBorrowerFirstName,
    applicationType,
    coBorrowerInformationRequired,
    requiredDocuments,
    isThereNonBorrowingCoOwner,
    homeOwnershipInformationComplete,
    fundingInformationIsRequired,
    fundingInformationComplete,
    autopaySectionIsRequired,
    autopaySectionIsCompleted,
    productName,
    additionalTabProps.isAdditionalInfoTabRequired,
    calculateAdditionalTabRemaining,
    dispatch,
    borrowerId,
    borrowerPayrollProviderIsDisabled,
    coBorrowerPayrollProviderIsDisabled,
    payrollProviderBorrowerVerificationStates,
    payrollProviderCoBorrowerVerificationStates,
    borrowerEmploymentInformationComplete,
    coBorrowerEmploymentInformationComplete,
    isTruvDisabled,
  ]);

  const setDashboardStepByStatus = useCallback(() => {
    if (currentDashboardStep !== null) {
      return;
    }

    switch (true) {
      case applicationStatus === ApplicationStatus.Active:
        return dispatch(setDashboardStep(DashboardStep.NewInquiry));
      case applicationStatus === ApplicationStatus.Pitched && productName === ApplicationProductName.SmallLoanProgram:
        return dispatch(setDashboardStep(DashboardStep.AcceptPosAutomatedOffer));
      case applicationStatus === ApplicationStatus.Pitched && applicationVariables!.hasAutomatedOffer:
        return dispatch(setDashboardStep(DashboardStep.AcceptHilAutomatedOffer));
      case applicationStatus === ApplicationStatus.Pitched:
        return dispatch(setDashboardStep(DashboardStep.AcceptOffer));
      case applicationStatus === ApplicationStatus.Purgatory:
      case applicationStatus === ApplicationStatus.Processing:
      case applicationStatus === ApplicationStatus.ReadyToSubmit:
      case applicationStatus === ApplicationStatus.Underwriting:
      case applicationStatus === ApplicationStatus.NeedMoreInfo:
      case applicationStatus === ApplicationStatus.NMICompleted:
      case applicationStatus === ApplicationStatus.PendingFunding:
      case applicationStatus === ApplicationStatus.Compliance:
      case applicationStatus === ApplicationStatus.Approved: {
        const firstAvailableTab = tabsPanelUploadDocsAndRequireInfo.find((tabInfo) => tabInfo.isExist);

        // set first available Dashboard step for uploading document
        if (!isUploadingDocumentStep && firstAvailableTab) {
          return dispatch(setDashboardStep(firstAvailableTab.id));
        }

        return;
      }
      default:
        return dispatch(setDashboardStep(DashboardStep.NoActiveApplication));
    }
  }, [
    applicationStatus,
    currentDashboardStep,
    dispatch,
    isUploadingDocumentStep,
    productName,
    tabsPanelUploadDocsAndRequireInfo,
  ]);

  const getSidebarAdditionInfo = () => {
    if (productName === ApplicationProductName.SmallLoanProgram) {
      return applicationStatus === ApplicationStatus.Pitched ? (
        <InformationalBoxWithBubble>
          <LoanOfferOptions />
        </InformationalBoxWithBubble>
      ) : null;
    } else {
      return isUploadingDocumentStep ? (
        <InformationalBoxWithBubble>
          <VerificationBoxPrompt />
        </InformationalBoxWithBubble>
      ) : null;
    }
  };

  const getOnClickEvent = (sidebarElement: DashboardSidebarElements) => {
    switch (sidebarElement) {
      case DashboardSidebarElements.MyApplication:
        return () => {
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: true }));
          dispatch(getApplicationInfoForDashboardThunk(borrowerId!))
            .unwrap()
            .finally(() => {
              dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: false }));
            });
          dispatch(setDashboardStep(null));
        };

      case DashboardSidebarElements.FAQs:
        return () => {
          if (applicationId) {
            dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          }

          dispatch(setDashboardStep(DashboardStep.FAQ));
        };

      case DashboardSidebarElements.AccountSettings:
        return async () => {
          dispatch(setDashboardStep(DashboardStep.AccountSettings));
          dispatch(setLoadingState({ type: LoadingType.AccountSettingUpdating, loading: true }));

          const account = await dispatch(getAccount()).unwrap();

          dispatch(setAccount({ email: account?.email, phone: account?.phones[0]?.value }));

          dispatch(setLoadingState({ type: LoadingType.AccountSettingUpdating, loading: false }));
        };

      case DashboardSidebarElements.LoanDetails:
        return async () => {
          dispatch(setDashboardStep(DashboardStep.LoanDetails));
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: true }));
          await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
          dispatch(setLoadingState({ type: LoadingType.DashboardPageUpdating, loading: false }));
        };

      default:
        return () => {};
    }
  };

  const getActiveSidebarElement = (step: DashboardStep | null) => {
    switch (step) {
      case DashboardStep.FAQ:
        return DashboardSidebarElements.FAQs;
      case DashboardStep.AccountSettings:
        return DashboardSidebarElements.AccountSettings;
      case DashboardStep.LoanDetails:
        return DashboardSidebarElements.LoanDetails;

      default:
        return DashboardSidebarElements.MyApplication;
    }
  };

  useEffect(() => {
    setDashboardStepByStatus();
  }, [setDashboardStepByStatus]);

  const handleWithdrawApplicationClick = () => {
    setIsDeclineOfferOpen(true);
  };

  const handleClosePopup = () => {
    setIsDeclineOfferOpen(false);
  };

  if (isIframe) {
    return <NewInquiry />;
  }

  const kbaBorrowerRequired = !applicationVariables?.borrower_kba_status && applicationVariables?.borrower_kba_required;
  const kbaCoborrowerRequired =
    !applicationVariables?.coborrower_kba_status && applicationVariables?.coborrower_kba_required;

  return (
    <div className={styles.dashboardContainer}>
      {(kbaBorrowerRequired || kbaCoborrowerRequired) && (
        <KbaPopup kbaBorrowerRequired={!!kbaBorrowerRequired} kbaCoborrowerRequired={!!kbaCoborrowerRequired} />
      )}
      <Sidebar
        sidebarElements={
          Object.values(DashboardSidebarElements)
            .filter((sidebarElement) => {
              if (sidebarElement === DashboardSidebarElements.LoanDetails) {
                return applicationStatus && STATUSES_FOR_LOAN_DETAILS.includes(applicationStatus);
              }

              return true;
            })
            .map((sidebarElement) => ({
              name: sidebarElement,
              onClick: getOnClickEvent(sidebarElement),
            })) as SidebarElement[]
        }
        activeElement={getActiveSidebarElement(currentDashboardStep)}
        borrowerName={`${firstName} ${lastName}`}
        borrowerEmail={email}
        additionalInfo={getSidebarAdditionInfo()}
      >
        {STATUSES_FOR_WITHDRAW_APPLICATION.includes(applicationStatus!) && (
          <Button
            onClick={handleWithdrawApplicationClick}
            className={styles.buttonWithdrawApplication}
            buttonType={ButtonType.Secondary}
          >
            {WITHDRAW_APPLICATION}
          </Button>
        )}
      </Sidebar>
      <div
        className={clsx(
          styles.dashboardContentContainer,
          currentDashboardStep === DashboardStep.AccountSettings && styles.overflowHidden,
        )}
      >
        {![DashboardStep.AccountSettings, DashboardStep.NoActiveApplication].includes(currentDashboardStep!) && (
          <DashboardTopInfo
            applicationId={displayId}
            loanAmount={loanAmount}
            label={getCurrentLabel(applicationStatus)}
            createdDate={createdDate}
            processorInfo={processorInfo}
          />
        )}
        {getDashboardFormHeader(
          currentDashboardStep,
          messageFromHFS,
          documentsUploadDueDate,
          tabsPanelUploadDocsAndRequireInfo,
        )}
        {dashboardPageUpdating ? <Loader /> : getPageByStep(currentDashboardStep)}
      </div>
      <DeclineOfferPopup isModalOpen={isDeclineOfferOpen} onClose={handleClosePopup} applicationId={applicationId!} />
      <MessageFromHfsPopup
        isOpen={messageFromHfsShowOnPortal || false}
        mainMessage={messageFromHFS!}
        onClose={handleCloseMessageFromHfsPopup}
      />
    </div>
  );
};

export default Dashboard;
